import { useQuery } from '@apollo/client/react';
import { GET_TARGET } from '@optools/services/operator-gateway/queries';
import type { TargetView } from '@optools/services/operator-gateway/types/generated/graphql';
import { createTargetId } from '@optools/tools/partner/utils/targets';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

type DataCenterTabSource = {
  channels: readonly { id: number }[];
  name: string;
};

type PartnerTabSource = {
  name: string;
  targets: readonly { name: string }[];
};

/** Mapping UX when the route is driven by the `target` search param / GET_TARGET. */
export function useTargetMappingSelection({
  activeDataCenterTab,
  dataCenters,
  partners,
  setActiveDataCenterTab,
  targetMap,
  targetParam,
}: {
  targetParam: string;
  targetMap: Map<string, TargetView>;
  dataCenters: readonly DataCenterTabSource[];
  partners: readonly PartnerTabSource[];
  activeDataCenterTab: string | null;
  setActiveDataCenterTab: Dispatch<SetStateAction<string | null>>;
}) {
  const getTargetResult = useQuery(GET_TARGET, {
    context: { skipErrorNotification: true },
    fetchPolicy: 'network-only',
    skip: !targetParam,
    variables: { name: targetParam },
  });

  const { error: targetError, refetch: targetRefetch } = getTargetResult;
  useEffect(() => {
    if (targetError) {
      toast.error('Target Error', {
        action: { label: 'Retry', onClick: () => targetRefetch() },
        description: targetError.message,
        duration: Infinity,
      });
    }
  }, [targetError, targetRefetch]);

  const currentTarget = getTargetResult.data?.getTarget;
  const targetQueryLoading = getTargetResult.loading;

  const [currentTargetChannelId, setCurrentTargetChannelId] = useState<number | null>(null);
  const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null);
  const [activePartnerTab, setActivePartnerTab] = useState<string | null>(null);
  /** True if channel was picked while GET_TARGET was loading; skips one post-load sync from Apollo. */
  const channelSelectedDuringTargetLoadRef = useRef(false);

  useEffect(() => {
    setCurrentTargetChannelId(null);
    setSelectedChannelId(null);
    channelSelectedDuringTargetLoadRef.current = false;
  }, [targetParam]);

  const handleSelectChannel = useCallback(
    (channelId: number | null) => {
      if (targetParam && targetQueryLoading) {
        channelSelectedDuringTargetLoadRef.current = true;
      }
      setSelectedChannelId((currentId) => (currentId === channelId ? null : channelId));
    },
    [targetParam, targetQueryLoading],
  );

  useEffect(() => {
    if (currentTarget) targetMap.set(createTargetId(currentTarget), currentTarget);

    const channelId = currentTarget?.channelId || null;

    setCurrentTargetChannelId(channelId);

    const activeDataCenterName = dataCenters.find(({ channels }) =>
      channels.some(({ id }) => id === channelId),
    )?.name;

    if (!activeDataCenterTab && activeDataCenterName) {
      setActiveDataCenterTab(activeDataCenterName);
    }

    const activePartnerName = partners.find(({ targets }) =>
      targets.some(({ name }) => name === currentTarget?.name),
    )?.name;

    if (!activePartnerTab && activePartnerName) {
      setActivePartnerTab(activePartnerName);
    }
  }, [
    activeDataCenterTab,
    activePartnerTab,
    currentTarget,
    dataCenters,
    partners,
    setActiveDataCenterTab,
    targetMap,
  ]);

  useEffect(() => {
    const channelId = currentTarget?.channelId ?? null;

    if (targetQueryLoading) {
      return;
    }

    if (channelSelectedDuringTargetLoadRef.current) {
      channelSelectedDuringTargetLoadRef.current = false;
      return;
    }

    // Default selection to server channel id when the user did not select a new channel during loading.
    setSelectedChannelId(channelId);
  }, [currentTarget, targetQueryLoading]);

  return {
    activePartnerTab,
    currentTarget,
    currentTargetChannelId,
    handleSelectChannel,
    selectedChannelId,
    targetQueryLoading,
  };
}
